import { createContext, useCallback, useContext, useState, type ReactNode } from "react";

// ── Types ────────────────────────────────────────────────────

type ToastType = "success" | "error" | "warning" | "info";

interface Toast {
  id: number;
  type: ToastType;
  message: string;
  duration: number;
}

interface ToastContextValue {
  /** Show a toast notification. */
  toast: (message: string, type?: ToastType, duration?: number) => void;
  /** Convenience shorthand: success toast. */
  success: (message: string) => void;
  /** Convenience shorthand: error toast. */
  error: (message: string) => void;
  /** Convenience shorthand: warning toast. */
  warn: (message: string) => void;
  /** Convenience shorthand: info toast. */
  info: (message: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

let _nextId = 1;

const TYPE_ICONS: Record<ToastType, string> = {
  success: "✅",
  error: "❌",
  warning: "⚠️",
  info: "ℹ️",
};

// ── Provider ─────────────────────────────────────────────────

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToast = useCallback(
    (message: string, type: ToastType = "info", duration = 4000) => {
      const id = _nextId++;
      setToasts((prev) => [...prev, { id, type, message, duration }]);
      if (duration > 0) {
        setTimeout(() => removeToast(id), duration);
      }
    },
    [removeToast],
  );

  const ctx: ToastContextValue = {
    toast: addToast,
    success: (m) => addToast(m, "success"),
    error: (m) => addToast(m, "error", 6000),
    warn: (m) => addToast(m, "warning", 5000),
    info: (m) => addToast(m, "info"),
  };

  return (
    <ToastContext.Provider value={ctx}>
      {children}

      {/* Toast container — renders at the top-right corner */}
      <div className="toast-container">
        {toasts.map((t) => (
          <div key={t.id} className={`toast toast-${t.type}`}>
            <span className="toast-icon">{TYPE_ICONS[t.type]}</span>
            <span className="toast-message">{t.message}</span>
            <button
              className="toast-close"
              onClick={() => removeToast(t.id)}
              aria-label="Fermer"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

// ── Hook ─────────────────────────────────────────────────────

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used within a <ToastProvider>");
  }
  return ctx;
}
